// Pixel Utilities - Facebook/Instagram, Snapchat, and Reddit
// File: src/utils/pixels.js

// Your Pixel IDs
const FACEBOOK_PIXEL_ID = '1052322916336184';
const SNAPCHAT_PIXEL_ID = 'acd1ff23-2d38-44d0-9a87-92a7dda1f8a1';
const REDDIT_PIXEL_ID = 'a2_gpaifeom17e2';

// Initialize Facebook Pixel
export const initFacebookPixel = () => {
  if (typeof window !== 'undefined') {
    // Check if pixel is already loaded to prevent duplicates
    if (window.fbq && window.fbq.loaded) {
      console.log('Facebook Pixel already initialized, skipping...');
      return;
    }

    // Facebook Pixel Code - ESLint compliant version
    // eslint-disable-next-line no-unused-expressions
    (function(f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function() {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(
      window,
      document,
      'script',
      'https://connect.facebook.net/en_US/fbevents.js'
    );

    window.fbq('init', FACEBOOK_PIXEL_ID);
    window.fbq('track', 'PageView');
    
    console.log('Facebook Pixel initialized successfully');
  }
};

// Initialize Snapchat Pixel
export const initSnapchatPixel = () => {
  if (typeof window !== 'undefined') {
    // Check if pixel is already loaded to prevent duplicates
    if (window.snaptr && window.snaptr.loaded) {
      console.log('Snapchat Pixel already initialized, skipping...');
      return;
    }

    // Snapchat Pixel Code
    // eslint-disable-next-line no-unused-expressions
    (function(e, t, n) {
      if (e.snaptr) return;
      var a = e.snaptr = function() {
        a.handleRequest ? a.handleRequest.apply(a, arguments) : a.queue.push(arguments);
      };
      a.queue = [];
      var s = 'script';
      var r = t.createElement(s);
      r.async = true;
      r.src = n;
      var u = t.getElementsByTagName(s)[0];
      u.parentNode.insertBefore(r, u);
    })(window, document, 'https://sc-static.net/scevent.min.js');

    window.snaptr('init', SNAPCHAT_PIXEL_ID, {
      'user_email': '__INSERT_USER_EMAIL__', // You can replace this dynamically if you have user email
      'uuid_c1': undefined // Pass user ID here when available for better matching
    });
    window.snaptr('track', 'PAGE_VIEW');
    
    console.log('Snapchat Pixel initialized successfully');
  }
};

// Track Facebook Events
export const trackFacebookEvent = (eventName, eventData = {}) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, eventData);
    console.log(`Facebook Pixel: Tracked ${eventName}`, eventData);
  } else {
    console.warn('Facebook Pixel not loaded');
  }
};

// Initialize Reddit Pixel
export const initRedditPixel = () => {
  if (typeof window !== 'undefined') {
    // Check if pixel is already loaded to prevent duplicates
    if (window.rdt && window.rdt.loaded) {
      console.log('Reddit Pixel already initialized, skipping...');
      return;
    }

    // Reddit Pixel Code
    // eslint-disable-next-line no-unused-expressions
    !function(w, d) {
      if (!w.rdt) {
        var p = w.rdt = function() {
          p.sendEvent ? p.sendEvent.apply(p, arguments) : p.callQueue.push(arguments);
        };
        p.callQueue = [];
        var t = d.createElement('script');
        t.src = 'https://www.redditstatic.com/ads/pixel.js';
        t.async = true;
        var s = d.getElementsByTagName('script')[0];
        s.parentNode.insertBefore(t, s);
      }
    }(window, document);

    window.rdt('init', REDDIT_PIXEL_ID);
    window.rdt('track', 'PageVisit');
    
    console.log('Reddit Pixel initialized successfully');
  }
};

// Track Snapchat Events
export const trackSnapchatEvent = (eventName, eventData = {}) => {
  if (typeof window !== 'undefined' && window.snaptr) {
    window.snaptr('track', eventName, eventData);
    console.log(`Snapchat Pixel: Tracked ${eventName}`, eventData);
  } else {
    console.warn('Snapchat Pixel not loaded');
  }
};

// Track Reddit Events
export const trackRedditEvent = (eventName, eventData = {}) => {
  if (typeof window !== 'undefined' && window.rdt) {
    window.rdt('track', eventName, eventData);
    console.log(`Reddit Pixel: Tracked ${eventName}`, eventData);
  } else {
    console.warn('Reddit Pixel not loaded');
  }
};

// Signup Event Tracking (Works for Facebook, Instagram, Snapchat, and Reddit ads)
// Facebook requires distinct price values across different CompleteRegistration events to optimize ad delivery.
// We assign different values based on registration method and user context to help Facebook identify high-value users.
export const trackSignup = (method = 'email', userId = null, additionalContext = {}) => {
  // Assign value based on expected user quality/conversion potential
  // This helps Facebook's algorithm optimize for higher-value users
  let signupValue = 1.00; // Base value

  // Adjust based on registration method (higher friction = higher intent)
  if (method === 'google') {
    signupValue = 2.50; // Google OAuth: lower friction, good conversion rate
  } else if (method === 'email') {
    signupValue = 3.00; // Email signup: higher friction = stronger intent
  }

  // Apply context-based modifiers to create naturally varying values
  if (additionalContext.language === 'BRL') {
    signupValue += 0.25; // Market-specific adjustment (Brazilian market)
  }

  // REAL modifier: User came from /upgrade or /chat (requires authentication, high-intent users)
  if (additionalContext.fromLandingPage === '/arena' || additionalContext.fromLandingPage === '/pt-br') {
    signupValue += 1.00; // Came from landing page = engaged visitor
  }

  // Round to 2 decimal places
  signupValue = Math.round(signupValue * 100) / 100;

  const eventData = {
    content_name: 'User Registration',
    content_category: 'Account',
    method: method,
    value: signupValue, // Dynamic value (will naturally create 5+ distinct values)
    currency: 'USD'
  };

  // Add user ID if available
  if (userId) {
    eventData.external_id = userId;
  }

  // Track on all platforms
  trackFacebookEvent('CompleteRegistration', eventData);
  trackSnapchatEvent('SIGN_UP', {
    uuid_c1: userId || undefined,
    user_email: userId ? `user_${userId}@pickstarai.com` : undefined,
    ...eventData
  });
  trackRedditEvent('SignUp', eventData);
};

// Lead Event (when someone starts signup process)
export const trackLead = () => {
  const eventData = {
    content_name: 'Signup Form Started',
    content_category: 'Account'
  };
  
  trackFacebookEvent('Lead', eventData);
  trackSnapchatEvent('START_CHECKOUT', eventData);
  trackRedditEvent('Lead', eventData);
};

// Login Event
export const trackLogin = (method = 'email') => {
  const eventData = {
    method: method
  };
  
  trackFacebookEvent('Login', eventData);
  trackSnapchatEvent('LOGIN', eventData);
  trackRedditEvent('Login', eventData);
};

// Custom Events for more detailed tracking
export const trackCustomEvent = (eventName, eventData = {}) => {
  // Track on all platforms
  trackFacebookEvent(eventName, eventData);
  trackSnapchatEvent(eventName, eventData);
  trackRedditEvent(eventName, eventData);
};

// Form Interaction Tracking
export const trackFormStart = () => {
  trackFacebookEvent('InitiateCheckout', {
    content_name: 'Registration Form',
    content_category: 'Account'
  });
};

// Email Input Focus (micro-conversion)
export const trackEmailFocus = () => {
  trackCustomEvent('EmailFieldFocused', {
    content_name: 'Email Input Started'
  });
};

// Google Button Click
export const trackGoogleButtonClick = () => {
  trackCustomEvent('GoogleSignupClicked', {
    content_name: 'Google OAuth Initiated'
  });
};

// ================ ENHANCED FACEBOOK PIXEL EVENTS ================

// Email Verification Events
export const trackEmailVerificationSent = (userId) => {
  trackFacebookEvent('EmailVerificationSent', {
    content_name: 'Verification Email',
    content_category: 'Account',
    user_id: userId,
    value: 0.5, // Mid-funnel conversion value
    currency: 'USD'
  });
};

export const trackEmailVerified = (userId, additionalContext = {}) => {
  // Base value for email verification
  let verificationValue = 1.00;

  // Apply context-based modifiers (same pattern as trackSignup)
  if (additionalContext.language === 'BRL') {
    verificationValue += 0.25; // Market-specific adjustment (Brazilian market)
  } else {
    verificationValue += 1.00; // US/English users - higher spending market
  }

  if (additionalContext.fromLandingPage === '/arena' || additionalContext.fromLandingPage === '/pt-br') {
    verificationValue += 0.50; // Came from landing page = engaged visitor
  }

  if (additionalContext.signupMethod === 'email') {
    verificationValue += 0.25; // Email signup (higher friction = stronger intent)
  }

  // Round to 2 decimal places
  verificationValue = Math.round(verificationValue * 100) / 100;

  trackFacebookEvent('EmailVerified', {
    content_name: 'Email Verified Successfully',
    content_category: 'Account',
    user_id: userId,
    value: verificationValue, // Dynamic value (creates 6 distinct values)
    currency: 'USD'
  });
};

// First Message Event (Key engagement metric)
export const trackFirstMessage = (userId, messageLength) => {
  trackFacebookEvent('FirstMessage', {
    content_name: 'First Chat Message Sent',
    content_category: 'Engagement',
    user_id: userId,
    message_length: messageLength,
    value: 2.0, // High value for engaged users
    currency: 'USD'
  });
};

// Star Coin Purchase Events
export const trackInitiateStarCoinPurchase = (packageId, price) => {
  trackFacebookEvent('InitiateCheckout', {
    content_name: 'Star Coins Package',
    content_ids: [packageId],
    content_type: 'product',
    value: price,
    currency: 'USD',
    num_items: 1
  });
};

export const trackStarCoinPurchase = (packageId, price, starCoinsAmount, userId) => {
  trackFacebookEvent('Purchase', {
    content_name: 'Star Coins Package',
    content_ids: [packageId],
    content_type: 'product',
    value: price,
    currency: 'USD',
    num_items: 1,
    contents: [{
      id: packageId,
      quantity: starCoinsAmount,
      item_price: price
    }],
    user_id: userId
  });
};

// Subscription Events
export const trackSubscriptionStart = (planType, price, userId) => {
  trackFacebookEvent('Subscribe', {
    content_name: `${planType} Subscription`,
    content_category: 'Subscription',
    value: price,
    currency: 'USD',
    predicted_ltv: price * 12, // Assuming monthly subscription
    user_id: userId
  });
};

// View Content Events for key pages
export const trackViewContent = (contentName, contentCategory) => {
  trackFacebookEvent('ViewContent', {
    content_name: contentName,
    content_category: contentCategory
  });
};

// Search Events (when users search for teams/games)
export const trackSearch = (searchQuery, searchType) => {
  trackFacebookEvent('Search', {
    search_string: searchQuery,
    content_category: searchType // 'team', 'game', 'player', etc.
  });
};

// Add to Wishlist (when users save/favorite something)
export const trackAddToWishlist = (itemName, itemType) => {
  trackFacebookEvent('AddToWishlist', {
    content_name: itemName,
    content_category: itemType,
    value: 0.5,
    currency: 'USD'
  });
};

// Contact Support Event
export const trackContact = (contactMethod) => {
  trackFacebookEvent('Contact', {
    content_name: 'Support Contact',
    content_category: contactMethod // 'chat', 'email', etc.
  });
};